import { bridgeTimeout } from './bridge-timeout.js';
import bridge, { EAdsFormats } from '@vkontakte/vk-bridge';
export class VkPlatformAdapter {
    platform = 'vk';
    backListeners = new Set();
    launchData = null;
    async readLaunchData() {
        if (this.launchData)
            return this.launchData;
        const query = window.location.search.slice(1);
        const launch = new URLSearchParams(query);
        // Preserve the signed bytes. Authentication still verifies the signature on the API.
        if (launch.has('sign') && launch.has('vk_user_id') && launch.has('vk_app_id')) {
            this.launchData = query;
            return query;
        }
        const params = await bridge.send('VKWebAppGetLaunchParams');
        this.launchData = new URLSearchParams(Object.entries(params)
            .filter((entry) => entry[1] !== undefined)
            .map(([key, value]) => [key, String(value)])).toString();
        return this.launchData;
    }
    async expandDesktopContainer(raw) {
        const platform = new URLSearchParams(raw).get('vk_platform') ?? '';
        if (!platform.startsWith('desktop_'))
            return;
        const availableWidth = window.screen.availWidth || window.screen.width || window.innerWidth;
        const width = Math.min(1400, Math.max(1000, availableWidth - 320));
        const height = Math.min(1000, Math.max(720, window.innerHeight));
        await bridge.send('VKWebAppResizeWindow', { width, height }).catch(() => undefined);
    }
    async initialize() {
        await bridge.send('VKWebAppInit');
        const raw = await this.readLaunchData();
        void this.expandDesktopContainer(raw);
    }
    async getLaunchData() {
        const raw = await this.readLaunchData();
        return {
            raw,
            startParameter: this.startParameter(raw),
            source: 'vk',
        };
    }
    async getCurrentUser() {
        const user = await bridge.send('VKWebAppGetUserInfo');
        return {
            id: String(user.id),
            firstName: user.first_name,
            ...(user.last_name ? { lastName: user.last_name } : {}),
            ...(user.photo_200 ? { avatarUrl: user.photo_200 } : {}),
            platform: 'vk',
        };
    }
    getStartParameter() {
        return this.startParameter(this.launchData ?? window.location.search.slice(1));
    }
    startParameter(raw) {
        const params = new URLSearchParams(raw);
        return params.get('vk_start_param') ?? this.locationHash() ?? params.get('vk_ref');
    }
    locationHash() {
        const hash = window.location.hash.replace(/^#/, '').trim();
        if (!hash || hash.length > 512)
            return null;
        try {
            return decodeURIComponent(hash);
        }
        catch {
            return null;
        }
    }
    async share(payload) {
        try {
            if (payload.mode === 'wall') {
                await bridgeTimeout(bridge.send('VKWebAppShowWallPostBox', {
                    message: `${payload.text}\n${payload.url}`,
                    attachments: payload.url,
                    ...(payload.imageUrl
                        ? { upload_attachments: [{ type: 'photo', link: payload.imageUrl }] }
                        : {}),
                }));
            }
            else if (payload.mode === 'story' && payload.imageDataUrl) {
                await bridgeTimeout(bridge.send('VKWebAppShowStoryBox', {
                    background_type: 'image',
                    blob: payload.imageDataUrl,
                    locked: true,
                    attachment: { type: 'url', text: 'open', url: payload.url },
                }));
            }
            else if (payload.mode === 'invite') {
                await bridgeTimeout(bridge.send('VKWebAppShowInviteBox'));
            }
            else {
                await bridgeTimeout(bridge.send('VKWebAppShare', { link: payload.url }));
            }
            return { shared: true, method: 'native' };
        }
        catch (error) {
            if (payload.mode === 'wall' || payload.mode === 'story') {
                const data = error && typeof error === 'object' && 'error_data' in error ? error.error_data : null;
                if (data && typeof data === 'object' && 'error_code' in data && data.error_code === 4)
                    return { shared: false, method: 'native' };
                throw new Error('VK не открыл публикацию. Повторите попытку или скопируйте ссылку.');
            }
            await navigator.clipboard.writeText(`${payload.text} ${payload.url}`);
            return { shared: true, method: 'clipboard' };
        }
    }
    async openExternal(url) {
        window.open(url, '_blank', 'noopener,noreferrer');
    }
    async joinCommunity(url) {
        const match = new URL(url).pathname.match(/^\/(?:club|public)(\d+)\/?$/);
        if (!match) {
            await this.openExternal(url);
            return false;
        }
        await bridge.send('VKWebAppJoinGroup', { group_id: Number(match[1]) });
        return true;
    }
    async requestNotifications() {
        if (!(await bridgeTimeout(bridge.supportsAsync('VKWebAppAllowNotifications'), 8_000)))
            return false;
        const result = await bridgeTimeout(bridge.send('VKWebAppAllowNotifications'));
        return result.result;
    }
    async haptic(type) {
        if (!(await bridge.supportsAsync('VKWebAppTapticImpactOccurred')))
            return;
        const style = type === 'success' || type === 'error' ? 'medium' : type;
        await bridge.send('VKWebAppTapticImpactOccurred', { style });
    }
    showBackButton() {
        // VK has no universal header BackButton API; the app renders its own 44px control.
    }
    hideBackButton() { }
    onBack(callback) {
        this.backListeners.add(callback);
        const handler = () => callback();
        window.addEventListener('popstate', handler);
        return () => {
            this.backListeners.delete(callback);
            window.removeEventListener('popstate', handler);
        };
    }
    async getViewport() {
        return { width: window.innerWidth, height: window.innerHeight, stable: true };
    }
    async showRewardedAd() {
        if (!(await bridge.supportsAsync('VKWebAppShowNativeAds')))
            return { completed: false };
        if (await bridge.supportsAsync('VKWebAppCheckNativeAds')) {
            const availability = await bridge
                .send('VKWebAppCheckNativeAds', {
                ad_format: EAdsFormats.REWARD,
                use_waterfall: true,
            })
                .catch(() => ({ result: false }));
            if (!availability.result)
                return { completed: false };
        }
        const result = await bridge.send('VKWebAppShowNativeAds', {
            ad_format: EAdsFormats.REWARD,
            use_waterfall: true,
        });
        return { completed: result.result };
    }
    async purchaseProduct(productId) {
        const result = await bridge.send('VKWebAppShowOrderBox', { type: 'item', item: productId });
        return {
            completed: result.status === 'success',
            status: result.status,
            ...(result.order_id ? { transactionId: result.order_id } : {}),
        };
    }
}
